import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, Radio, Activity, MessageCircle, CreditCard, Menu, Bell, Shield, TrendingUp, Moon, Gift } from 'lucide-react';
import SettingsPageModern from './SettingsPageModern';
import { NotificationsPanelImproved } from './NotificationsPanelImproved';
import ReferEarnLoggedIn from './ReferEarnLoggedIn';
import { PromotionalOfferChecker } from './PromotionalOfferChecker';
import { SuperInterestPopup } from './SuperInterestPopup';
import { DailyInterestModal } from './DailyInterestModal';
import { NewInterestModal } from './NewInterestModal';
import { toast } from 'sonner';
import MobileSidebarContent from './MobileSidebarContent';
import MatchesTab from './MatchesTab';
import { LiveTab } from './tabs/LiveTab';
import { PlanStatusNavbar } from './PlanStatusNavbar';
import MyProfilePage from './MyProfilePage';
import { API_URL } from '../lib/api-client';
import { useAppDispatch, useAppSelector } from '../store/store';
import { useUserDisplay } from '../hooks/useUserDisplay';
import { fetchConversations } from '../store/slices/chatSlice';
import { fetchProfile } from '../store/slices/profileSlice';
import { fetchDiscoveryProfiles } from '../store/slices/discoverySlice';
import { fetchUnreadCount } from '../store/slices/notificationSlice';
import { fetchSettings } from '../store/slices/settingsSlice';
import { fetchCurrentSubscription } from '../store/slices/planSlice';
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from './ui/sheet';
import { generateProfileSlug } from '../utils/seo';
import { useTheme ,themes} from './ThemeProvider';
import ViewProfilePage from './ViewProfilePage';
import { Button } from './ui/button';
import { AtootbandanLogo } from './AtootbandanLogo';
import ActivityTabEnhanced from './ActivityTabEnhanced';
import MessagesTabBeautiful from './tabs/MessagesTabBeautiful';
import { PlansTabComplete } from './tabs/PlansTabComplete';
import { Badge } from './ui/badge';
import DesktopNavbar from './DesktopNavbar';
import DesktopMatchesLayout from './DesktopMatchesLayout';
import AdminDashboardReal from './AdminDashboardReal';
import { loadUser } from '../store/slices/authSlice';
import { logger } from '../utils/logger';

interface MobileAppProps {
  onLogout: () => void;
  onNavigate: (screen: 'home' | 'login' | 'register' | 'app' | 'admin' | 'blogs' | 'community' | 'refer-earn' | 'search' | 'help' | 'change-password' | 'edit-profile' | 'premium-marketplace' | 'terms' | 'advanced-matching' | 'premium-shop' | 'marketing-panel' | 'kundli-maker') => void;
}

type Tab = 'matches' | 'activity' | 'messages' | 'plans' | 'live';

export default function MobileApp({ onLogout, onNavigate }: MobileAppProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [viewingProfile, setViewingProfile] = useState<any>(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showNotificationsPanel, setShowNotificationsPanel] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showMyProfile, setShowMyProfile] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showReferEarn, setShowReferEarn] = useState(false);
  const [showPlanStatus, setShowPlanStatus] = useState(false);
  const [similarFilter, setSimilarFilter] = useState<'similar' | 'suggested' | undefined>(undefined);
  const { theme } = useTheme();
  const currentTheme = themes[theme];
  const dispatch = useAppDispatch();
  const { getProfilePhoto, getFullName, getInitials } = useUserDisplay();
  
  // Get real data from Redux with defensive checks
  const { conversations, lastUpdate } = useAppSelector(state => state?.chat || { conversations: [], lastUpdate: 0 });
  const { user } = useAppSelector(state => state?.auth || { user: null });
  const { currentSubscription } = useAppSelector(state => state?.plan || { currentSubscription: null });
  const notificationUnreadCount = useAppSelector(state => state?.notification?.unreadCount || 0);
  const { settings } = useAppSelector(state => state?.settings || { settings: null });
  const [notificationCount, setNotificationCount] = useState(0);
  
  // Get profile data to check if we have cached data
  const { data: profileData } = useAppSelector(state => state?.profile || { data: null });
  
  const messageCount = useMemo(() => {
    const count = Array.isArray(conversations) 
      ? conversations.filter(conv => (conv?.unreadCount || 0) > 0).length
      : 0;
    
    return count;
  }, [conversations, lastUpdate]);

  // Get current tab from URL
  const getCurrentTab = (): Tab => {
    const path = location.pathname.replace('/app/', '').replace('/app', '');
    const validTabs: Tab[] = ['matches', 'activity', 'messages', 'plans', 'live'];
    return (validTabs.includes(path as Tab) ? path : 'matches') as Tab;
  };

  const activeTab = getCurrentTab();

  // Update URL when tab changes
  const setActiveTab = (tab: Tab) => {
    navigate(`/app/${tab}`, { replace: true });
  };

  // Sync URL on mount
  useEffect(() => {
    if (location.pathname === '/app' || location.pathname === '/app/') {
      navigate('/app/matches', { replace: true });
    }
  }, [location.pathname, navigate]);

  // Handle URL query parameters (e.g., filter=similar)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const filterParam = searchParams.get('filter');
    if (filterParam === 'similar') {
      setSimilarFilter('similar');
      // Navigate to matches tab if not already there
      if (activeTab !== 'matches') {
        setActiveTab('matches');
      }
    } else if (filterParam === 'suggested') {
      setSimilarFilter('suggested');
      // Navigate to matches tab if not already there
      if (activeTab !== 'matches') {
        setActiveTab('matches');
      }
    } else {
      setSimilarFilter(undefined);
    }
  }, [location.search]);

  // Load user and profile data on mount with proper error handling
  useEffect(() => {
    // Only load data once
    if (dataLoaded) return;
    
    const loadData = async () => {
      try {
        // Check if we have cached data in Redux - if yes, skip loading screen
        const hasUser = !!user;
        const hasProfile = !!profileData;
        const hasSettings = !!settings;
        
        // OPTIMIZATION: Hide loading immediately if we have any cached data
        if (hasUser || hasProfile || hasSettings) {
          setIsInitializing(false);
        }
        
        // Minimal delay for auth context sync (reduced from 50ms to 10ms)
        await new Promise(resolve => setTimeout(resolve, 10));
        
        // Load user first (quick operation) - with timeout
        const userPromise = Promise.race([
          dispatch(loadUser()).unwrap().catch(() => null),
          new Promise((resolve) => setTimeout(resolve, 300)) // 300ms max
        ]);
        
        await userPromise;
        
        // After user loads, hide loading screen immediately
        setIsInitializing(false);
        
        // OPTIMIZATION: Check if we have valid cached discovery data - skip fetch if valid
        const discoveryState = (window as any).__REDUX_STORE__?.getState?.()?.discovery;
        const hasValidCache = discoveryState?.lastFetched > 0 && 
                            (Date.now() - discoveryState.lastFetched) < discoveryState.cacheExpiry &&
                            discoveryState?.profiles?.length > 0;
        
        // Then load other data in background (user won't see loading screen)
        Promise.allSettled([
          dispatch(fetchProfile()).unwrap().catch(() => null),
          dispatch(fetchConversations()).unwrap().catch(() => null),
          dispatch(fetchCurrentSubscription()).unwrap().catch(() => null),
          dispatch(fetchSettings()).unwrap().catch(() => null),
          dispatch(fetchUnreadCount()).unwrap().catch(() => null),
          // Only fetch discovery if no valid cache exists
          hasValidCache ? Promise.resolve() : dispatch(fetchDiscoveryProfiles({ isBackground: true })).unwrap().catch(() => null)
        ]).then(() => {
          setDataLoaded(true);
        });
        
      } catch (error) {
        logger.error('Error loading app data:', error);
        setIsInitializing(false);
        setDataLoaded(true);
      }
    };
    
    loadData();
  }, [dispatch, dataLoaded, user, profileData, settings]);

  // SMS Invite Auto-Trigger on App Load (only for logged in users in native app)
  useEffect(() => {
    const triggerSmsInviteIfNeeded = async () => {
      // Only run in native app and if user is logged in and on matches tab
      if (!(window as any).isNativeApp || !user || activeTab !== 'matches') {
        return;
      }

      try {
        // Check if user has already sent SMS
        if (user.smsSent === true) {
          return;
        }

        // Check settings from Redux store
        if (!settings?.features) {
          return;
        }

        const smsInviteEnabled = settings.features.smsInviteEnabled;
        const smsInviteAutoTrigger = settings.features.smsInviteAutoTrigger;
        
        // Only show if both SMS invite and auto-trigger are enabled
        if (smsInviteEnabled !== true || smsInviteAutoTrigger !== true) {
          return;
        }

        // Trigger SMS modal via React Native WebView
        if ((window as any).ReactNativeWebView) {
          const message = {
            type: 'AUTO_SHOW_SMS_MODAL',
            referralCode: user.referralCode || 'ATOOTBANDAN',
            userName: user.fullName || user.firstName || 'Your Friend',
            smsSent: user.smsSent || false
          };
          
          (window as any).ReactNativeWebView.postMessage(JSON.stringify(message));
        }
      } catch (error) {
        // Silent fail
      }
    };

    // Delay to ensure settings are loaded
    const timeoutId = setTimeout(triggerSmsInviteIfNeeded, 2000);
    
    return () => clearTimeout(timeoutId);
  }, [user, settings, activeTab]);

  const fetchNotificationCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        // No token - reset count
        setNotificationCount(0);
        return;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Fetch unread notification count
      const { data } = await axios.get(`${API_URL}/notifications/unread-count`, { timeout: 3000 });
      
      if (data.success && data.data.count !== undefined) {
        setNotificationCount(data.data.count);
      }
    } catch (error) {
      // Silent fail - reset count
      setNotificationCount(0);
    }
  };

  const handleViewProfile = (profile: any, interestContext?: 'received' | 'sent' | 'accepted' | null) => {
    // Check if user is authenticated
    const token = localStorage.getItem('token');
    
    if (!token) {
      toast.error('Please login to view profiles', {
        action: {
          label: 'Login',
          onClick: () => {
            onLogout();
          }
        }
      });
      return;
    }

    // Use URL routing for profile view
    // Extract profile information from various possible structures
    const firstName = profile.firstName || profile.userId?.firstName || profile.name?.split(' ')[0] || 'User';
    const lastName = profile.lastName || profile.userId?.lastName || profile.name?.split(' ')[1] || '';
    
    // CRITICAL FIX: Always use profile._id (not userId._id) as the primary ID
    // The profile._id is the actual profile document ID in the database
    let profileId = profile._id || profile.id;
    
    // If profile._id is missing, try other fields (but this should rarely happen)
    if (!profileId) {
      profileId = profile.userId?._id;
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ [MobileApp] Using userId._id as fallback (profile._id was missing)');
      }
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 [MobileApp] Viewing profile:', {
        firstName,
        lastName,
        profileId,
        profileStructure: {
          _id: profile._id,
          id: profile.id,
          'userId (might be ObjectId string or object)': profile.userId,
          'userId._id (if userId is populated)': profile.userId?._id
        },
        fullProfile: profile
      });
    }
    
    if (profileId) {
      const slug = generateProfileSlug(firstName, lastName, profileId);
      logger.log('📍 [MobileApp] Navigating to:', `/profile/${slug}`);
      navigate(`/profile/${slug}`);
    } else {
      logger.error('❌ [MobileApp] Unable to extract profile ID from:', profile);
      toast.error('Unable to view profile. Please try again.');
    }
  };

  // 🔥 NEW: Auto-switch to first available tab if current tab is disabled
  useEffect(() => {
    if (!settings?.features) return;
    
    const isCurrentTabDisabled = 
      (activeTab === 'matches' && settings.features.discoverTabEnabled === false) ||
      (activeTab === 'live' && !settings.features.liveTabEnabled) ||
      (activeTab === 'activity' && settings.features.activityTabEnabled === false) ||
      (activeTab === 'messages' && settings.features.messagesTabEnabled === false) ||
      (activeTab === 'plans' && settings.features.plansTabEnabled === false);

    if (isCurrentTabDisabled) {
      // Find first available tab
      if (settings.features.discoverTabEnabled !== false) {
        setActiveTab('matches');
      } else if (settings.features.liveTabEnabled) {
        setActiveTab('live');
      } else if (settings.features.activityTabEnabled !== false) {
        setActiveTab('activity');
      } else if (settings.features.messagesTabEnabled !== false) {
        setActiveTab('messages');
      } else if (settings.features.plansTabEnabled !== false) {
        setActiveTab('plans');
      }
    }
  }, [activeTab, settings?.features]);

  const handleBackToMatches = () => {
    setViewingProfile(null);
  };

  const handleNavigateToNotifications = () => {
    setShowNotificationsPanel(true);
    setSidebarOpen(false);
  };

  const handleNavigateToSettings = () => {
    setShowSettings(true);
    setSidebarOpen(false);
  };

  const handleNavigateToProfile = () => {
    setShowMyProfile(true);
    setSidebarOpen(false);
  };

  const handleNavigateToEditProfile = () => {
    onNavigate('edit-profile');
  };

  const handleNavigateToChangePassword = () => {
    onNavigate('change-password');
  };

  const handleNavigateToHelp = () => {
    onNavigate('help');
  };

  const handleNavigateToTerms = () => {
    onNavigate('terms');
  };

  const handleBackFromSettings = () => {
    setShowSettings(false);
  };

  const handleBackFromNotifications = () => {
    setShowNotifications(false);
  };

  const handleBackFromMyProfile = () => {
    setShowMyProfile(false);
  };

  // Desktop sidebar navigation items - dynamically filtered based on feature settings
  const desktopNavItems = [
    ...(settings?.features?.discoverTabEnabled !== false ? [
      { id: 'matches' as Tab, icon: Heart, label: 'Discover', description: 'Find your match' }
    ] : []),
    ...(settings?.features?.liveTabEnabled ? [
      { id: 'live' as Tab, icon: Radio, label: 'Live', description: 'Active members', badge: 'Live' }
    ] : []),
    ...(settings?.features?.activityTabEnabled !== false ? [
      { id: 'activity' as Tab, icon: Activity, label: 'Activity', description: 'Recent updates' }
    ] : []),
    ...(settings?.features?.messagesTabEnabled !== false ? [
      { id: 'messages' as Tab, icon: MessageCircle, label: 'Messages', description: 'Chat with matches', count: messageCount }
    ] : []),
    ...(settings?.features?.plansTabEnabled !== false ? [
      { id: 'plans' as Tab, icon: CreditCard, label: 'Premium', description: 'Upgrade account' }
    ] : []),
  ];

  const desktopExtraItems = [
    // HIDDEN: Advanced Search - Will enable in future if required
    // { icon: Search, label: 'Advanced Search', onClick: () => onNavigate('advanced-matching') },
    // HIDDEN: Community
    // { icon: UsersIcon, label: 'Community', onClick: () => onNavigate('community') },
    { icon: Moon, label: 'Kundli Maker', onClick: () => onNavigate('kundli-maker'), badge: 'Free' },
    { icon: Gift, label: 'Refer & Earn', onClick: () => onNavigate('refer-earn') },
    // HIDDEN: Premium Shop
    // { icon: Sparkles, label: 'Premium Shop', onClick: () => onNavigate('premium-shop') },
  ];

  // Show special pages (mobile and desktop)
  // OPTIMIZATION: Keep main app mounted and use CSS to hide it instead of unmounting
  const showingOverlay = showSettings || showNotifications || showMyProfile || viewingProfile || showAdminPanel || showReferEarn;

  // Show loading screen while initializing
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-pink-500 mx-auto mb-6"></div>
          <h2 className="text-xl text-gray-800 mb-2">Setting up your profile...</h2>
          <p className="text-gray-600">This will only take a moment</p>
        </div>
      </div>
    );
  }

  // Main App View
  return (
    <>
      {/* Mobile View */}
      <div className="md:hidden h-screen max-h-screen flex flex-col bg-gray-50 overflow-hidden max-w-full w-full">
        {/* Mobile Header */}
        <header 
          className={`text-white bg-gradient-to-r ${currentTheme.primary} px-4 py-3 flex items-center justify-between shadow-md sticky top-0 z-40 flex-shrink-0`}
          style={{
            background: currentTheme.gradient
          }}
        >
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/20">
                <Menu className="size-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-80">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <SheetDescription className="sr-only">
                Access your profile, settings, and other app features
              </SheetDescription>
              <MobileSidebarContent
                onNavigate={onNavigate}
                onLogout={onLogout}
                onSettings={handleNavigateToSettings}
                onNotifications={handleNavigateToNotifications}
                onProfile={handleNavigateToProfile}
                onClose={() => setSidebarOpen(false)}
              />
            </SheetContent>
          </Sheet>

          <div className="flex items-center gap-2 text-white">
            <AtootbandanLogo size="sm" showText={false} className="text-white" />
            <span className="text-lg font-medium">Atootbandan</span>
          </div>

          <div className="flex items-center gap-2">
            {/* Plan Status Button - Mobile */}
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20"
              onClick={() => setShowPlanStatus(true)}
              title="View Plan Status"
            >
              <CreditCard className="size-6" />
            </Button>

            {/* Admin Panel Button - Only show for admins */}
            {user?.role === 'admin' && (
              <Button
                className="bg-gradient-to-r from-amber-600 to-rose-600 hover:from-amber-700 hover:to-rose-700 text-white"
                onClick={() => setShowAdminPanel(true)}
              >
                <Shield className="size-4 mr-2" />
                Admin Panel
              </Button>
            )}
            
            {/* Marketing Manager Panel Button */}
            {user?.role === 'marketing_manager' && (
              <Button
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                onClick={() => onNavigate('marketing-manager')}
              >
                <Shield className="size-4 mr-2" />
                Manager Panel
              </Button>
            )}
            
            {/* Marketing Panel Button - Only show for marketing users */}
            {(user?.role === 'marketing' || user?.role === 'marketing_user') && (
              <Button
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                onClick={() => onNavigate('marketing-panel')}
              >
                <TrendingUp className="size-4 mr-2" />
                Marketing Panel
              </Button>
            )}
            
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20 relative"
              onClick={handleNavigateToNotifications}
            >
              <Bell className="size-6" />
              {notificationUnreadCount > 0 && (
                <Badge className="absolute -top-1 -right-1 size-5 flex items-center justify-center p-0 bg-amber-400 border-2 border-white text-xs">
                  {notificationUnreadCount}
                </Badge>
              )}
            </Button>
          </div>
        </header>

        {/* Mobile Content */}
        <div className="flex-1 min-h-0 overflow-y-auto w-full max-w-full pb-20">
          <div className={activeTab === 'matches' && settings?.features?.discoverTabEnabled !== false ? 'w-full min-h-full' : 'hidden'}>
            <DesktopMatchesLayout onViewProfile={handleViewProfile} initialFilter={similarFilter} />
          </div>
          <div className={activeTab === 'live' && settings?.features?.liveTabEnabled ? 'h-full overflow-y-auto' : 'hidden'}>
            <LiveTab onViewProfile={handleViewProfile} />
          </div>
          <div className={activeTab === 'activity' && settings?.features?.activityTabEnabled !== false ? 'h-full overflow-y-auto' : 'hidden'}>
            <ActivityTabEnhanced 
              onViewProfile={handleViewProfile} 
              onNavigateToPlans={() => setActiveTab('plans')}
              onViewAllSuggested={() => navigate('/app?tab=discovery&filter=suggested')}
            />
          </div>
          <div className={activeTab === 'messages' && settings?.features?.messagesTabEnabled !== false ? 'h-full overflow-hidden w-full max-w-full' : 'hidden'}>
            <MessagesTabBeautiful onNavigateToPlans={() => setActiveTab('plans')} />
          </div>
          <div className={activeTab === 'plans' && settings?.features?.plansTabEnabled !== false ? 'h-full overflow-y-auto' : 'hidden'}>
            <PlansTabComplete />
          </div>
        </div>

        {/* Mobile Bottom Navigation */}
        <nav className="bg-white border-t flex items-center justify-around px-2 py-2 flex-shrink-0">
          {settings?.features?.discoverTabEnabled !== false && (
            <Button
              variant="ghost"
              className={`flex flex-col items-center gap-1 h-auto py-2 px-4 ${
                activeTab === 'matches' ? 'text-rose-600' : 'text-gray-600'
              }`}
              onClick={() => setActiveTab('matches')}
            >
              <Heart className={`size-6 ${activeTab === 'matches' ? 'fill-current' : ''}`} />
              <span className="text-xs">Discover</span>
            </Button>
          )}
          {settings?.features?.liveTabEnabled && (
            <Button
              variant="ghost"
              className={`flex flex-col items-center gap-1 h-auto py-2 px-2 ${
                activeTab === 'live' ? 'text-rose-600' : 'text-gray-600'
              }`}
              onClick={() => setActiveTab('live')}
            >
              <Radio className="size-6" />
              <span className="text-xs">Live</span>
            </Button>
          )}
          {settings?.features?.activityTabEnabled !== false && (
            <Button
              variant="ghost"
              className={`flex flex-col items-center gap-1 h-auto py-2 px-4 ${
                activeTab === 'activity' ? 'text-rose-600' : 'text-gray-600'
              }`}
              onClick={() => setActiveTab('activity')}
            >
              <Activity className="size-6" />
              <span className="text-xs">Activity</span>
            </Button>
          )}
          {settings?.features?.messagesTabEnabled !== false && (
            <Button
              variant="ghost"
              className={`flex flex-col items-center gap-1 h-auto py-2 px-4 relative ${
                activeTab === 'messages' ? 'text-rose-600' : 'text-gray-600'
              }`}
              onClick={() => setActiveTab('messages')}
            >
              <MessageCircle className="size-6" />
              {messageCount > 0 && (
                <Badge className="absolute top-1 right-2 size-5 flex items-center justify-center p-0 bg-rose-600 text-xs">
                  {messageCount}
                </Badge>
              )}
              <span className="text-xs">Messages</span>
            </Button>
          )}
          {settings?.features?.plansTabEnabled !== false && (
            <Button
              variant="ghost"
              className={`flex flex-col items-center gap-1 h-auto py-2 px-4 ${
                activeTab === 'plans' ? 'text-rose-600' : 'text-gray-600'
              }`}
              onClick={() => setActiveTab('plans')}
            >
              <CreditCard className="size-6" />
              <span className="text-xs">Premium</span>
            </Button>
          )}
        </nav>
      </div>

      {/* Desktop View */}
      <div className="hidden md:flex md:flex-col h-screen bg-gray-50">
        {/* Desktop Top Navbar */}
        <DesktopNavbar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onNavigate={onNavigate}
          onLogout={onLogout}
          onShowNotifications={handleNavigateToNotifications}
          onShowProfile={handleNavigateToProfile}
          onShowSettings={handleNavigateToSettings}
          settings={settings}
        />

        {/* Desktop Content Area */}
        <div className="flex-1 flex flex-col overflow-y-auto">
          <div className={activeTab === 'matches' && settings?.features?.discoverTabEnabled !== false ? 'flex-1 flex flex-col min-h-0 overflow-y-auto' : 'hidden'}>
            <DesktopMatchesLayout onViewProfile={handleViewProfile} initialFilter={similarFilter} />
          </div>
          <div className={activeTab === 'live' && settings?.features?.liveTabEnabled ? 'flex-1 flex flex-col overflow-hidden' : 'hidden'}>
            <LiveTab onViewProfile={handleViewProfile} />
          </div>
          <div className={activeTab === 'activity' && settings?.features?.activityTabEnabled !== false ? 'flex-1 flex flex-col overflow-hidden' : 'hidden'}>
            <ActivityTabEnhanced 
              onViewProfile={handleViewProfile} 
              onNavigateToPlans={() => setActiveTab('plans')}
              onViewAllSuggested={() => navigate('/app?tab=discovery&filter=suggested')}
            />
          </div>
          <div className={activeTab === 'messages' && settings?.features?.messagesTabEnabled !== false ? 'flex-1 flex flex-col overflow-hidden' : 'hidden'}>
            <MessagesTabBeautiful onNavigateToPlans={() => setActiveTab('plans')} />
          </div>
          <div className={activeTab === 'plans' && settings?.features?.plansTabEnabled !== false ? 'flex-1 flex flex-col overflow-hidden' : 'hidden'}>
            <PlansTabComplete />
          </div>
        </div>
      </div>

      {/* Notifications Panel */}
      <NotificationsPanelImproved
        isOpen={showNotificationsPanel} 
        onClose={() => setShowNotificationsPanel(false)}
        onNavigateToMessages={(chatId, userId) => {
          setShowNotificationsPanel(false);
          setActiveTab('messages');
          // TODO: If chatId is provided, open that specific chat
        }}
        onNavigateToActivity={() => {
          setShowNotificationsPanel(false);
          setActiveTab('activity');
        }}
        onViewProfile={(profile) => {
          setShowNotificationsPanel(false);
          handleViewProfile(profile);
        }}
      />

      {/* Plan Status Panel */}
      <PlanStatusNavbar
        isOpen={showPlanStatus}
        onClose={() => setShowPlanStatus(false)}
        onNavigateToPlans={() => {
          setShowPlanStatus(false);
          setActiveTab('plans');
        }}
      />

      {/* Overlay Pages - Render on top without unmounting main app */}
      {showSettings && (
        <div className="fixed inset-0 z-50 bg-white">
          <SettingsPageModern
            onBack={handleBackFromSettings}
            onNavigateToEditProfile={handleNavigateToEditProfile}
            onNavigateToChangePassword={handleNavigateToChangePassword}
            onNavigateToHelp={handleNavigateToHelp}
            onNavigateToTerms={handleNavigateToTerms}
            onLogout={onLogout}
          />
        </div>
      )}

      {showNotifications && (
        <div className="fixed inset-0 z-50 bg-white">
          <NotificationsPageReal onBack={handleBackFromNotifications} />
        </div>
      )}

      {showMyProfile && (
        <div className="fixed inset-0 z-50 bg-white">
          <MyProfilePage onBack={handleBackFromMyProfile} onEdit={handleNavigateToEditProfile} />
        </div>
      )}

      {viewingProfile && (
        <div className="fixed inset-0 z-50 bg-white">
          <ViewProfilePage
            profile={viewingProfile} 
            onBack={handleBackToMatches}
            interestContext={viewingProfile._interestContext}
          />
        </div>
      )}

      {showAdminPanel && (
        <div className="fixed inset-0 z-50 bg-white">
          <AdminDashboardReal onBack={() => setShowAdminPanel(false)} />
        </div>
      )}

      {showReferEarn && (
        <div className="fixed inset-0 z-50 bg-white">
          <ReferEarnLoggedIn onBack={() => setShowReferEarn(false)} />
        </div>
      )}

      {/* Promotional Offer Checker - Shows modal if user has active promotional order */}
      <PromotionalOfferChecker />

      {/* Super Interest Popup - Shows when someone super likes you */}
      <SuperInterestPopup onViewProfile={handleViewProfile} />

      {/* Daily Interest Modal - Shows daily interests */}
      <DailyInterestModal />

      {/* New Interest Modal - Shows new interests */}
      <NewInterestModal />
    </>
  );
}